import * as React from "react";
import { makeStyles, Modal, Backdrop, CircularProgress, useMediaQuery, TextField, InputAdornment } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import SuccessDialog from "./SuccessDialog";
import { DiscountReturn, ChamberMemberReturn, UserType, WhereToRedeemEnum, useDeleteDiscountMutation } from "../../graphql/types";
import { RedirectUri, useEnumStrings } from "../enumStrings";
import { useHistory } from "react-router-dom";
import LogoImage from "./utilities/LogoImage";
import useGetChamberFromUserIdInAuth from "./Hooks/useGetChamberFromUserIdInAuth";
import { useGetOfferedToText } from "./Hooks/useGetOfferedToText";
import CommonTable, { HeadCell } from "./CommonTable";
import { getLocalTimestamp } from "../pages/Root/RootAdmin";

const EditIcon = require("../../images/edit-black-icon.png").default;
const DeleteIcon = require("../../images/delete-logo-icon-white.png").default;
const TrashIconWhite = require("../../images/trash-icon-white.svg").default;

const useStyles = makeStyles(() => ({
  // Delete dialog
  modalRoot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialogCard: {
    position: "relative",
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    padding: "40px 32px 36px",
    width: "380px",
    maxWidth: "90vw",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    outline: "none",
  },
  closeButton: {
    position: "absolute",
    top: "10px",
    right: "10px",
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "#D32F2F",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": {
      backgroundColor: "#B71C1C",
    },
  },
  trashCircle: {
    width: "110px",
    height: "110px",
    borderRadius: "50%",
    backgroundColor: "#D32F2F",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "24px",
  },
  trashIcon: {
    width: "50px",
    height: "50px",
    objectFit: "contain",
  },
  dialogTitle: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#111111",
    margin: "0 0 12px 0",
    textAlign: "center",
  },
  dialogMessage: {
    fontSize: "15px",
    color: "#444444",
    margin: "0 0 10px 0",
    textAlign: "center",
    lineHeight: 1.5,
  },
  dialogWarning: {
    fontSize: "14px",
    color: "#444444",
    fontStyle: "italic",
    margin: "0 0 28px 0",
    textAlign: "center",
  },
  dialogDeleteButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    width: "100%",
    padding: "14px 24px",
    borderRadius: "12px",
    border: "none",
    backgroundColor: "#D32F2F",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: 700,
    color: "#FFFFFF",
    letterSpacing: "1px",
    "&:hover": {
      backgroundColor: "#B71C1C",
    },
  },
  dialogDeleteIcon: {
    width: "20px",
    height: "20px",
    objectFit: "contain",
  },
  // Table actions
  actionsCell: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  editButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "8px 14px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#DEE2E6",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
    color: "#222222",
    whiteSpace: "nowrap",
    width: "100%",
    "&:hover": {
      backgroundColor: "#CDD1D6",
    },
  },
  deleteButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "8px 14px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#D32F2F",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
    color: "#FFFFFF",
    whiteSpace: "nowrap",
    width: "100%",
    "&:hover": {
      backgroundColor: "#B71C1C",
    },
  },
  actionIcon: {
    width: "16px",
    height: "16px",
    objectFit: "contain",
  },
  memberAndPerkCell: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  memberNameText: {
    fontWeight: 600,
    fontSize: "15px",
    color: "#111111",
    lineHeight: 1.3,
  },
  perkTitleText: {
    fontWeight: 500,
    fontSize: "15px",
    color: "#6A6A6A",
    lineHeight: 1.3,
  },
  successText: {
    fontSize: "36px",
    lineHeight: "1.35",
    color: "#111111",
    textAlign: "center",
    fontWeight: 700,
    margin: 0,
    "@media (max-width: 768px)": {
      fontSize: "32px",
    },
  },
  // ─── Mobile card styles ─────────────────────────────────────────────────
  mobileList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    paddingBottom: "16px",
  },
  mobileSearchBar: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    width: "100%",
    marginBottom: "12px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "& fieldset": { borderColor: "#000" },
      "&.Mui-focused fieldset": { borderColor: "#000", borderWidth: "2px" },
    },
  },
  perkCard: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    border: "1px solid #E8E8E8",
    padding: "14px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  perkCardTop: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  perkCardTitle: {
    fontWeight: 700,
    fontSize: "14px",
    color: "#111",
    flex: 1,
    lineHeight: 1.3,
  },
  perkCardMeta: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "6px",
  },
  perkCardMetaItem: {
    display: "flex",
    flexDirection: "row" as const,
    alignItems: "baseline",
    gap: "6px",
    flexWrap: "wrap" as const,
    minWidth: 0,
  },
  perkCardMetaLabel: {
    fontSize: "12px",
    color: "#555",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
  },
  perkCardMetaValue: {
    fontSize: "13px",
    color: "#222",
    fontWeight: 500,
  },
  perkStatusActive: {
    display: "inline-block",
    fontSize: "12px",
    fontWeight: 700,
    color: "#2E7D32",
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
  },
  perkStatusExpired: {
    display: "inline-block",
    fontSize: "12px",
    fontWeight: 700,
    color: "#C62828",
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
  },
  perkCardActions: {
    display: "flex",
    gap: "8px",
  },
  perkEditBtn: {
    flex: 1,
    minHeight: "44px",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "12px",
    textTransform: "none" as const,
    backgroundColor: "#DEE2E6",
    color: "#222",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "8px 10px",
    lineHeight: 1.2,
    textAlign: "center" as const,
    minWidth: 0,
  },
  perkDeleteBtn: {
    flex: 1,
    minHeight: "44px",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "12px",
    textTransform: "none" as const,
    backgroundColor: "#D32F2F",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "8px 10px",
    lineHeight: 1.2,
    textAlign: "center" as const,
    minWidth: 0,
  },
  // ────────────────────────────────────────────────────────────────────────
}));

type OmittedDiscount = Pick<DiscountReturn, "id" | "title" | "views" | "createdAtUtcMilli" | "expiryTimeUtcMilli" | "redeemSettings" | "isPrivate" | "memberOnly" | "isAvailableToAllMembers"> & {
  chamberMemberInfo?: Pick<ChamberMemberReturn, "id" | "name" | "logo"> | null;
};
type AllDiscountDataType = OmittedDiscount[];
type TableDiscountDataType = OmittedDiscount & {
  chamberMemberName: string;
  offeredToText: string;
  localCreationDate: number;
  endDate: number;
  redemptionMethodText: string;
};

// Helper function to get redemption method display text
const getRedemptionMethodText = (
  whereToRedeem: WhereToRedeemEnum | undefined,
  enumStrings: ReturnType<typeof useEnumStrings>
): string => {
  switch (whereToRedeem) {
    case WhereToRedeemEnum.InPerson:
      return enumStrings.inPerson;
    case WhereToRedeemEnum.Online:
      return enumStrings.online;
    case WhereToRedeemEnum.ContactCompany:
      return enumStrings.contactCompany;
    default:
      return "-";
  }
};

export default function FullPageDiscountTable({
  userType,
  allDiscountData,
  onDeleteSuccess,
}: {
  userType: UserType;
  allDiscountData: AllDiscountDataType;
  onDeleteSuccess?: () => void;
}) {
  const classes = useStyles();
  const history = useHistory();
  const getOfferedToText = useGetOfferedToText();
  const userIdInAuthChamberInfo = useGetChamberFromUserIdInAuth();
  const enumStrings = useEnumStrings();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [mobileSearch, setMobileSearch] = React.useState("");

  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [selectedDiscountId, setSelectedDiscountId] = React.useState<string | null>(null);
  const [deleteSuccessOpen, setDeleteSuccessOpen] = React.useState(false);

  const [deleteDiscount] = useDeleteDiscountMutation();

  // Transform data for table display
  const tableData: TableDiscountDataType[] = React.useMemo(() => {
    if (!allDiscountData) {
      return [];
    }

    return allDiscountData.map((discountData) => ({
      ...discountData,
      chamberMemberName: discountData.chamberMemberInfo?.name || "",
      offeredToText: getOfferedToText(discountData),
      localCreationDate: discountData.createdAtUtcMilli || 0,
      endDate: discountData.expiryTimeUtcMilli || 0,
      redemptionMethodText: getRedemptionMethodText(discountData.redeemSettings?.whereToRedeem, enumStrings),
    }));
  }, [allDiscountData, getOfferedToText, enumStrings]);

  const getEditRoute = React.useCallback(() => {
    if (userType === UserType.ChamberAdmin) {
      return RedirectUri.editDiscountPageChamberAdmin;
    }
    return RedirectUri.editDiscountChamberMemberAdmin;
  }, [userType]);

  // Handle row click
  const handleRowClick = React.useCallback(
    (discountInfo: TableDiscountDataType) => {
      history.push(getEditRoute(), discountInfo);
    },
    [history, getEditRoute]
  );

  const handleEditClick = React.useCallback(
    (e: React.MouseEvent, discountInfo: TableDiscountDataType) => {
      e.stopPropagation();
      history.push(getEditRoute(), discountInfo);
    },
    [history, getEditRoute]
  );

  const handleDeleteClick = React.useCallback(
    (e: React.MouseEvent, discountId: string) => {
      e.stopPropagation();
      setSelectedDiscountId(discountId);
      setDeleteModalOpen(true);
    },
    []
  );

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!selectedDiscountId) return;
    setDeleteLoading(true);
    try {
      const refetchQueries =
        userType === UserType.ChamberAdmin
          ? ["getAllDiscountsFromChamberId", "getAllDeletedDiscountsFromChamberId"]
          : ["getAllDiscountsFromChamberMemberId"];
      await deleteDiscount({
        variables: { id: selectedDiscountId },
        refetchQueries,
      });
      setDeleteModalOpen(false);
      setSelectedDiscountId(null);
      if (onDeleteSuccess) {
        onDeleteSuccess();
      } else {
        setDeleteSuccessOpen(true);
        setTimeout(() => setDeleteSuccessOpen(false), 5000);
      }
    } catch (err) {
      console.error("Error deleting perk:", err);
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteDiscount, selectedDiscountId, onDeleteSuccess]);

  const isMember = userType === UserType.ChamberMemberAdmin;

  // Column configuration
  const columns: HeadCell[] = React.useMemo(() => {
    const cols: HeadCell[] = [];

    if (!isMember) {
      cols.push(
        {
          id: "memberLogo",
          label: "",
          sortable: false,
          width: "80px",
        },
        {
          id: "chamberMemberName",
          label: "Member Name and Perk Title",
          description: "List of member companies offering perks",
          sortable: true,
        }
      );
    } else {
      cols.push({
        id: "title",
        label: enumStrings.memberManagePerks.perkTitle,
        description: enumStrings.memberManagePerks.perkTitleDesc,
        sortable: true,
      });
    }

    cols.push(
      {
        id: "offeredToText",
        label: enumStrings.ChamberAdminMangeMember.offeredToText,
        description: enumStrings.ChamberAdminMangeMember.offeredToTextDesc,
        sortable: true,
      },
      {
        id: "redemptionMethodText",
        label: enumStrings.ChamberAdminMangeMember.redemptionMethod,
        description: enumStrings.ChamberAdminMangeMember.redemptionMethodDesc,
        sortable: true,
      },
      {
        id: "views",
        label: enumStrings.ChamberAdminMangeMember.views,
        description: enumStrings.ChamberAdminMangeMember.viewsDesc,
        sortable: true,
      }
    );

    if (isMember) {
      cols.push(
        {
          id: "localCreationDate",
          label: enumStrings.memberManagePerks.createdDate,
          description: enumStrings.memberManagePerks.createdDateDesc,
          sortable: true,
        },
        {
          id: "endDate",
          label: enumStrings.memberManagePerks.endDate,
          description: enumStrings.memberManagePerks.endDateDesc,
          sortable: true,
        }
      );
    }

    cols.push({
      id: "actions",
      label: enumStrings.memberManagePerks.actions,
      sortable: false,
      width: "200px",
    });

    return cols;
  }, [isMember, enumStrings]);

  // Custom cell renderer
  const renderCell = (
    column: HeadCell,
    row: TableDiscountDataType,
    rowIndex: number
  ) => {
    switch (column.id) {
      case "memberLogo":
        return (
          <LogoImage
            logoSource={row?.chamberMemberInfo?.logo || ""}
            size="normal"
            squared
            key={row.id}
            chamberName={row.chamberMemberName}
          />
        );
      case "chamberMemberName":
        return (
          <div className={classes.memberAndPerkCell}>
            <div className={classes.memberNameText}>{row.chamberMemberName}</div>
            <div className={classes.perkTitleText}>{row.title}</div>
          </div>
        );
      case "title": {
        const isExpired = row.endDate ? row.endDate < Date.now() : false;
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div className={classes.memberNameText}>{row.title}</div>
            {/* Member Dashboard design: every perk shows Active or Expired. */}
            {row.endDate || userType === UserType.ChamberMemberAdmin ? (
              <span style={{ fontSize: "12px", fontWeight: 700, color: isExpired ? "#E53935" : "#4CAF50" }}>
                {isExpired ? "Expired" : "Active"}
              </span>
            ) : null}
          </div>
        );
      }
      case "offeredToText":
        return row.offeredToText;
      case "redemptionMethodText":
        return row.redemptionMethodText;
      case "views":
        // Show random number between 50 and 200 for demonstration purposes for sandbox chamber
        return userIdInAuthChamberInfo?.isSandbox
          ? Math.floor(Math.random() * 150) + 50
          : row.views || 0;
      case "localCreationDate":
        return row.localCreationDate
          ? getLocalTimestamp(row.localCreationDate, true)
          : "-";
      case "endDate": {
        if (!row.endDate) return userType === UserType.ChamberMemberAdmin ? "" : enumStrings.memberManagePerks.noExpiry;
        return getLocalTimestamp(row.endDate, true);
      }
      case "actions":
        return (
          <div className={classes.actionsCell}>
            <button
              className={classes.editButton}
              onClick={(e) => handleEditClick(e, row)}
            >
              <img src={EditIcon} alt="edit" className={classes.actionIcon} />
              {isMember
                ? enumStrings.memberManagePerks.updatePerk
                : enumStrings.listAllChamberDiscounts.updateMemberPerks}
            </button>
            <button
              className={classes.deleteButton}
              onClick={(e) => handleDeleteClick(e, row.id)}
            >
              <img src={DeleteIcon} alt="delete" className={classes.actionIcon} />
              {isMember
                ? enumStrings.memberManagePerks.deletePerk
                : enumStrings.listAllChamberDiscounts.deletePerk}
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Modal
        className={classes.modalRoot}
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <div className={classes.dialogCard}>
          {/* Close X button */}
          <button
            className={classes.closeButton}
            onClick={() => setDeleteModalOpen(false)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </button>

          {/* Red circle with trash icon */}
          <div className={classes.trashCircle}>
            <img src={TrashIconWhite} alt="delete" style={{ width: "48px", height: "48px" }} />
          </div>

          {/* Title */}
          <p className={classes.dialogTitle}>
            {enumStrings.listAllChamberDiscounts.deletePerk}
          </p>

          {/* Message */}
          <p className={classes.dialogMessage}>
            {enumStrings.listAllChamberDiscounts.deleteConfirmation}
          </p>

          {/* Warning */}
          <p className={classes.dialogWarning}>
            {enumStrings.listAllChamberDiscounts.cannotBeUndone}
          </p>

          {/* Delete button */}
          {deleteLoading ? (
            <CircularProgress style={{ color: "#D32F2F" }} />
          ) : (
            <button
              className={classes.dialogDeleteButton}
              onClick={handleDeleteConfirm}
            >
              <img src={DeleteIcon} alt="delete" className={classes.dialogDeleteIcon} />
              {enumStrings.listAllChamberDiscounts.deleteButton}
            </button>
          )}
          {userType === UserType.ChamberMemberAdmin && !deleteLoading && (
            <button type="button" onClick={() => setDeleteModalOpen(false)}
              style={{ marginTop: 12, minWidth: 180, background: "#0A0A0A", color: "#FFFFFF", border: "none", borderRadius: 10, padding: "14px 0", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
              Cancel
            </button>
          )}
        </div>
      </Modal>
      <SuccessDialog
        open={deleteSuccessOpen}
        onClose={() => setDeleteSuccessOpen(false)}
        message={enumStrings.toastMessages.memberPerkDeletedSuccess}
      />
      {isMobile ? (() => {
        const lower = mobileSearch.toLowerCase();
        const filtered = lower
          ? tableData.filter((row) =>
              (row.title || "").toLowerCase().includes(lower) ||
              (row.chamberMemberName || "").toLowerCase().includes(lower)
            )
          : tableData;
        return (
          <div className={classes.mobileList}>
            <TextField
              className={classes.mobileSearchBar}
              variant="outlined"
              size="small"
              placeholder={isMember ? enumStrings.memberManagePerks.searchPlaceholder : enumStrings.ChamberAdminMangeMember.searchPlaceholder}
              value={mobileSearch}
              onChange={(e) => setMobileSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#666", fontSize: "18px" }} />
                  </InputAdornment>
                ),
              }}
            />
            {filtered.map((row) => {
              const isExpired = row.endDate ? row.endDate < Date.now() : false;
              const { dateLine: createdDateLine } = row.localCreationDate
                ? { dateLine: getLocalTimestamp(row.localCreationDate, true) }
                : { dateLine: "-" };
              const endDateText = !row.endDate
                ? enumStrings.memberManagePerks.noExpiry
                : getLocalTimestamp(row.endDate, true);
              return (
                <div key={row.id} className={classes.perkCard}>
                  {/* Top: logo (chamber admin) or title */}
                  <div className={classes.perkCardTop}>
                    {!isMember && (
                      <LogoImage
                        logoSource={row.chamberMemberInfo?.logo || ""}
                        size="normal"
                        squared
                        chamberName={row.chamberMemberName}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      {!isMember && (
                        <div style={{ fontSize: "12px", color: "#666", fontWeight: 500 }}>{row.chamberMemberName}</div>
                      )}
                      <div className={classes.perkCardTitle}>{row.title}</div>
                      {row.endDate !== undefined && (
                        <span className={isExpired ? classes.perkStatusExpired : classes.perkStatusActive}>
                          {isExpired ? "Expired" : "Active"}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Meta info */}
                  <div className={classes.perkCardMeta}>
                    <div className={classes.perkCardMetaItem}>
                      <span className={classes.perkCardMetaLabel}>{enumStrings.ChamberAdminMangeMember.offeredToText}:</span>
                      <span className={classes.perkCardMetaValue}>{row.offeredToText}</span>
                    </div>
                    <div className={classes.perkCardMetaItem}>
                      <span className={classes.perkCardMetaLabel}>{enumStrings.ChamberAdminMangeMember.redemptionMethod}:</span>
                      <span className={classes.perkCardMetaValue}>{row.redemptionMethodText}</span>
                    </div>
                    <div className={classes.perkCardMetaItem}>
                      <span className={classes.perkCardMetaLabel}>{enumStrings.ChamberAdminMangeMember.views}:</span>
                      <span className={classes.perkCardMetaValue}>
                        {userIdInAuthChamberInfo?.isSandbox
                          ? Math.floor(Math.random() * 150) + 50
                          : row.views || 0}
                      </span>
                    </div>
                    {isMember && (
                      <>
                        <div className={classes.perkCardMetaItem}>
                          <span className={classes.perkCardMetaLabel}>{enumStrings.memberManagePerks.createdDate}:</span>
                          <span className={classes.perkCardMetaValue}>{createdDateLine}</span>
                        </div>
                        <div className={classes.perkCardMetaItem}>
                          <span className={classes.perkCardMetaLabel}>{enumStrings.memberManagePerks.endDate}:</span>
                          <span className={classes.perkCardMetaValue}>{endDateText}</span>
                        </div>
                      </>
                    )}
                  </div>
                  {/* Action buttons */}
                  <div className={classes.perkCardActions}>
                    <button
                      className={classes.perkEditBtn}
                      onClick={(e) => handleEditClick(e, row)}
                    >
                      <img src={EditIcon} alt="edit" style={{ width: "14px", height: "14px" }} />
                      {isMember ? enumStrings.memberManagePerks.updatePerk : enumStrings.listAllChamberDiscounts.updateMemberPerks}
                    </button>
                    <button
                      className={classes.perkDeleteBtn}
                      onClick={(e) => handleDeleteClick(e, row.id)}
                    >
                      <img src={DeleteIcon} alt="delete" style={{ width: "14px", height: "14px" }} />
                      {isMember ? enumStrings.memberManagePerks.deletePerk : enumStrings.listAllChamberDiscounts.deletePerk}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })() : (
      <CommonTable<TableDiscountDataType>
        data={tableData}
        columns={columns}
        defaultSortColumn={isMember ? "title" : "chamberMemberName"}
        defaultSortOrder="asc"
        searchPlaceholder={
          isMember
            ? enumStrings.memberManagePerks.searchPlaceholder
            : enumStrings.ChamberAdminMangeMember.searchPlaceholder
        }
        searchPlaceholderColor="#000000"
        searchFields={isMember ? ["title"] : ["chamberMemberName", "title"]}
        onRowClick={handleRowClick}
        rowKeyField="id"
        renderCell={renderCell}
      />
      )}
    </>
  );
}
